import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { IHistoryEntry, IRecordTypeDefinition } from '@platform/core/types';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useMembersQuery } from '@platform/state/api';

/**
 * Значення «було → стало» в історії мовою інтерфейсу (прототип v4 показує підписи, не коди): статус і варіант поля — підписом модуля,
 * особа — ім'ям учасника, стан і роль доручення — підписами вкладки «Доручення»; решта (текст, дата, число) — як є; порожнє — «—».
 */
export function useHistoryValue(ws: string, rt: IRecordTypeDefinition | undefined): (entry: IHistoryEntry, value: string | null | undefined) => string {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const { data: members = [] } = useMembersQuery(ws);
  return useCallback((entry: IHistoryEntry, value: string | null | undefined) => {
    if (value == null || value === '') return '—';
    const field = entry.field ?? '';
    if (field === 'assignment.status') return t(`recordPage.assignments.statuses.${value}`, { defaultValue: value });
    if (field === 'assignment') {
      const [role = '', ...who] = value.split(': ');
      const label = t(`recordPage.assignments.roles.${role}`, { defaultValue: role });
      return who.length ? `${label}: ${who.join(': ')}` : label;
    }
    if (!rt) return value;
    if (entry.kind === 'status_changed' || field === 'status') return strings.status(rt.key, value, rt.statuses.list.find((s) => s.value === value)?.label);
    const def = rt.fields.find((f) => f.key === field);
    if (def?.type === 'select') return strings.option(rt.key, field, value, def.options?.find((o) => o.value === value)?.label);
    if (def?.type === 'user') return members.find((m) => m.user.id === value)?.user.name ?? value;
    return value;
  }, [t, strings, rt, members]);
}
